import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { publicGuard } from './core/guards/public.guard';
import { accessGuard, landingGuard } from './core/guards/access.guard';
import { trialTermsGuard } from './core/guards/trial-terms.guard';

/**
 * SmartClock main routes.
 *
 * Layout: auth (public routes) → login
 * Layout: main (protected routes) → home + SmartClock views (V1-V24, deferred)
 *
 * Source of truth: docs/SMARTCLOCK-WEBAPP-REQUIREMENTS.md §3 (route map).
 *
 * Acceso (SMARTCLOCK_API/docs/access/CONTRACT.md §6): cada ruta de /app declara
 * en `data` lo que exige — `permission` (TODAS), `anyPermission` (AL MENOS
 * UNA), `staff` (clave de staff), `inspector: true` — y `accessGuard`
 * (canActivateChild de /app) lo evalúa sumando los requisitos de los padres.
 * `scope` separa variantes de una misma pantalla por alcance de datos
 * (empresa / propio / EST), no es un permiso; una pantalla nueva no lo usa:
 * resuelve de quién son los datos con DataScopeService (un componente por
 * concepto, ver «Dirección del Trabajo»). Una ruta sin requisitos sólo
 * exige autenticación. `data.menu` la vuelve entrada del menú lateral (ver
 * shared/constants/menu.ts): la entrada aparece sólo si la ruta abre.
 */
export const routes: Routes = [
  {
    path: '',
    redirectTo: '/app',
    pathMatch: 'full',
  },
  // Public routes (login, password reset, etc.)
  {
    path: '',
    loadComponent: () => import('./layouts/auth-layout/auth-layout').then((m) => m.AuthLayout),
    canActivate: [publicGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./views/auth/login/login.component').then((m) => m.Login),
      },
      // Contratación self-service v2 (wizard público, registro diferido)
      {
        path: 'signup',
        loadComponent: () => import('./views/auth/signup/signup.component').then((m) => m.Signup),
      },
    ],
  },
  // Retorno del pago del signup: PÚBLICA pero SIN publicGuard (se llega con
  // o sin sesión; tras el claim navega a /app). Mismo AuthLayout (sin footer
  // DT por el prefijo /signup).
  {
    path: '',
    loadComponent: () => import('./layouts/auth-layout/auth-layout').then((m) => m.AuthLayout),
    children: [
      {
        path: 'signup/result',
        loadComponent: () =>
          import('./views/auth/signup-result/signup-result.component').then((m) => m.SignupResult),
      },
      // «Revisa y paga» de una contratación comercial (link del correo
      // sales-payment-link, ?key=): pública y sin publicGuard, porque el
      // cliente puede abrirla con otra sesión iniciada en el navegador.
      {
        path: 'contratar/:draftId',
        loadComponent: () =>
          import('./views/auth/sales-checkout/sales-checkout.component').then(
            (m) => m.SalesCheckout,
          ),
      },
      // Restablecimiento/activación de contraseña (destino de los correos
      // password-reset y account-invite ?welcome=1) — sin guards: se llega
      // con o sin sesión desde el email.
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./views/auth/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent,
          ),
      },
      // Enrolamiento worker-led (destino del correo enrollment-invite)
      {
        path: 'enroll',
        loadComponent: () =>
          import('./views/auth/enroll/enroll.component').then((m) => m.EnrollComponent),
      },
      {
        path: 'confirm-email',
        loadComponent: () =>
          import('./views/auth/confirm-email/confirm-email.component').then(
            (m) => m.ConfirmEmailComponent,
          ),
      },
      // Link de pago de un cobro del período (botón «Pagar con Flow» del
      // correo): público, con o sin sesión; la llave `k` del link autoriza.
      // Sin footer DT por el prefijo /pagar (AuthLayout). `pagar/resultado`
      // (retorno de Flow sin pago reconocido) va ANTES de `pagar/:invoiceId`
      // para que «resultado» no se lea como id. Spec:
      // docs/spec-first/specs/public-pay.spec.md.
      {
        path: 'pagar/resultado',
        loadComponent: () =>
          import('./views/billing/public-pay-result/public-pay-result.component').then(
            (m) => m.PublicPayResult,
          ),
      },
      {
        path: 'pagar/:invoiceId',
        loadComponent: () =>
          import('./views/billing/public-pay/public-pay.component').then((m) => m.PublicPay),
      },
      {
        path: 'pagar/:invoiceId/resultado',
        loadComponent: () =>
          import('./views/billing/public-pay-result/public-pay-result.component').then(
            (m) => m.PublicPayResult,
          ),
      },
      // Acceso de soporte («Ver como», SMARTCLOCK_API support-access.spec §5):
      // el backoffice abre /support/start#code=… en una pestaña nueva, que
      // queda en modo soporte (sessionStorage). Sin guards: se llega con o sin
      // una sesión normal en otras pestañas, y ninguna de las dos la toca.
      {
        path: 'support/start',
        loadComponent: () =>
          import('./views/support/support-start/support-start.component').then(
            (m) => m.SupportStartComponent,
          ),
      },
      {
        path: 'support/ended',
        loadComponent: () =>
          import('./views/support/support-ended/support-ended.component').then(
            (m) => m.SupportEndedComponent,
          ),
      },
      // «El servicio de SmartClock terminó» (término de suscripción): destino
      // de 403 COMPANY_TERMINATED en el login y en cualquier pantalla. Sin
      // guards: llega quien acaba de perder la sesión (CompanyTerminationService
      // la cierra) o un login rechazado. Spec:
      // docs/spec-first/specs/subscription-termination.spec.md.
      {
        path: 'servicio-terminado',
        loadComponent: () =>
          import('./views/billing/service-ended/service-ended.component').then(
            (m) => m.ServiceEndedComponent,
          ),
      },
    ],
  },
  // Protected application shell (V4-V24)
  {
    path: 'app',
    loadComponent: () => import('./layouts/main-layout/main-layout').then((m) => m.MainLayout),
    canActivate: [authGuard],
    // trialTermsGuard va DESPUÉS de accessGuard: si no puede abrir la ruta, gana su sin-acceso
    canActivateChild: [accessGuard, trialTermsGuard],
    // Sin esto, authGuard solo corre al ENTRAR a /app — navegar entre hijos
    // (ej. reportes de asistencia → jornada diaria) no lo re-ejecuta, porque
    // Angular no vuelve a evaluar los guards de un padre que ya está activo.
    // Con un token vencido a mitad de sesión, eso dejaba seguir navegando
    // dentro de la app sin detectar la sesión inválida hasta el próximo
    // refresh. 'always' fuerza a authGuard a correr en cada navegación.
    runGuardsAndResolvers: 'always',
    children: [
      {
        // Inicio: redirige al landing del usuario (ROLE_LANDING si lo puede
        // abrir, si no la primera entrada del menú). Sin ninguno, explica si
        // no tiene módulos habilitados o si sus permisos no cargaron.
        path: '',
        pathMatch: 'full',
        canActivate: [landingGuard],
        data: { titleKey: 'menu.noAccess', noAccessMode: 'landing' },
        loadComponent: () =>
          import('./views/no-access/no-access.component').then((m) => m.NoAccessComponent),
      },
      {
        // Destino de toda denegación del guard (sólo exige autenticación).
        path: 'sin-acceso',
        data: { titleKey: 'menu.noAccess', noAccessMode: 'denied' },
        loadComponent: () =>
          import('./views/no-access/no-access.component').then((m) => m.NoAccessComponent),
      },
      {
        // Sin `staff`: la web no tiene selector de empresa para el equipo SmartClock (trabaja en el
        // Backoffice y entra a una empresa con «Ver como», cuya sesión ya es la del usuario de la
        // empresa). Lo mismo en los reportes DT y en Auditoría: sin empresa no tienen datos.
        path: 'dashboard',
        data: {
          titleKey: 'menu.dashboard',
          permission: 'dashboard.view',
          menu: {
            resource: 'dashboard',
            labelKey: 'menu.dashboard',
            icon: 'dashboard',
            module: 'inicio',
            order: 10,
          },
        },
        loadComponent: () =>
          import('./views/dashboard/dashboard.component').then((m) => m.Dashboard),
      },
      {
        path: 'clocking',
        data: {
          titleKey: 'menu.clocking',
          permission: 'self.clocking.create',
          menu: {
            resource: 'self.clocking',
            group: '/app/dt-clockings',
            labelKey: 'menu.clocking',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/attendance/clocking/clocking.component').then((m) => m.Clocking),
      },
      {
        // Comprobante propio (tras marcar o desde Mi asistencia); mismo
        // criterio que GET /clocking-receipts/folio/:folio.
        path: 'receipt/:folio',
        data: {
          titleKey: 'menu.receipt',
          anyPermission: ['receipts.view', 'self.attendance.view'],
        },
        loadComponent: () =>
          import('./views/attendance/receipt/receipt.component').then((m) => m.Receipt),
      },
      {
        // Sólo su módulo («Mi Asistencia · Ver»): ver marcaciones de la empresa
        // no la abre, para que el menú calce con la matriz del backoffice.
        // «Mi Asistencia»: la MISMA pantalla de marcaciones, con lo propio de la sesión
        // (`ownData`), aunque el rol sea de empresa (supervisor).
        path: 'my-attendance',
        data: {
          titleKey: 'menu.myAttendance',
          permission: 'self.attendance.view',
          ownData: true,
          menu: {
            resource: 'self.attendance',
            group: '/app/dt-clockings',
            labelKey: 'menu.myAttendance',
            order: 2,
          },
        },
        loadComponent: () =>
          import('./views/attendance/clockings/clockings.component').then(
            (m) => m.ClockingsComponent,
          ),
      },
      {
        // Asistencia del día en tiempo real: la abre quien ve las marcaciones
        // de la empresa o sus alertas (el supervisor monitorea por alertas).
        path: 'daily-panel',
        data: {
          titleKey: 'menu.dailyPanel',
          permission: 'dailyPanel.view',
          menu: {
            resource: 'dailyPanel',
            labelKey: 'menu.dailyPanel',
            icon: 'monitor_heart',
            module: 'general',
            order: 20,
          },
        },
        loadComponent: () =>
          import('./views/attendance/daily-panel/daily-panel.component').then((m) => m.DailyPanel),
      },
      {
        path: 'employees',
        // Pantalla de gestión: exige ver Y editar. El supervisor tiene
        // `employees.view` por las lecturas del API (consultas puntuales),
        // pero nunca tuvo esta pantalla.
        data: {
          titleKey: 'menu.employees',
          permission: ['employees.view', 'employees.update'],
          scope: 'company',
          menu: {
            resource: 'employees',
            group: '/app/management',
            labelKey: 'menu.employees',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/management/employees/employees.component').then((m) => m.Employees),
      },
      {
        path: 'employees/:id',
        data: {
          titleKey: 'menu.employees',
          permission: ['employees.view', 'employees.update'],
          scope: 'company',
        },
        loadComponent: () =>
          import('./views/management/employee-detail/employee-detail.component').then(
            (m) => m.EmployeeDetail,
          ),
      },
      {
        path: 'workplaces',
        data: {
          titleKey: 'menu.managementSub.establishments',
          permission: 'workplaces.view',
          menu: {
            resource: 'workplaces',
            group: '/app/management',
            labelKey: 'menu.managementSub.establishments',
            order: 3,
          },
        },
        loadComponent: () =>
          import('./views/management/workplaces/workplaces.component').then((m) => m.Workplaces),
      },
      {
        path: 'shifts',
        data: {
          titleKey: 'menu.shifts',
          permission: 'shifts.view',
          menu: { resource: 'shifts', group: '/app/management', labelKey: 'menu.shifts', order: 5 },
        },
        loadComponent: () =>
          import('./views/management/shifts/shifts.component').then((m) => m.ShiftsComponent),
      },
      {
        // Jornadas pactadas (22-10-2026): abre con ver; crear y editar los decide la pantalla con
        // workSchedules.create y .update. Contrato §13 del API.
        path: 'work-schedules',
        data: {
          titleKey: 'menu.managementSub.workSchedules',
          permission: 'workSchedules.view',
          menu: {
            resource: 'workSchedules',
            group: '/app/management',
            labelKey: 'menu.managementSub.workSchedules',
            order: 6,
          },
        },
        loadComponent: () =>
          import('./views/management/work-schedules/work-schedules.component').then(
            (m) => m.WorkSchedulesComponent,
          ),
      },
      {
        path: 'positions',
        data: {
          titleKey: 'menu.positions',
          permission: 'positions.view',
          menu: {
            resource: 'positions',
            group: '/app/management',
            labelKey: 'menu.positions',
            order: 4,
          },
        },
        loadComponent: () =>
          import('./views/management/positions/positions.component').then(
            (m) => m.PositionsComponent,
          ),
      },
      {
        path: 'correct-clockings',
        data: {
          titleKey: 'menu.correctClockings',
          permission: 'clockings.view',
          menu: {
            resource: 'clockings',
            group: '/app/dt-clockings',
            labelKey: 'menu.correctClockings',
            order: 3,
          },
        },
        loadComponent: () =>
          import('./views/attendance/clockings/clockings.component').then(
            (m) => m.ClockingsComponent,
          ),
      },
      {
        // Mantenedor de dispositivos de marcación (requerimiento §4), contra
        // GET/POST/PATCH/DELETE /devices del API (HC-01 retirado).
        // `devices.view` (su propio módulo): con `workplaces.view` aparecía en
        // Gestión a quien solo debía ver Establecimientos (pre-lanzamiento).
        path: 'devices',
        data: {
          titleKey: 'menu.devices',
          permission: 'devices.view',
          menu: {
            resource: 'devices',
            group: '/app/management',
            labelKey: 'menu.devices',
            order: 2,
          },
        },
        loadComponent: () =>
          import('./views/management/devices/devices.component').then((m) => m.DevicesComponent),
      },
      {
        // §17 — mantenedor de EST/contratistas: es donde se marca una empresa
        // como EST. CRUD real sobre `external-companies`; la escritura la
        // restringe el API.
        path: 'external-companies',
        data: {
          titleKey: 'menu.externalCompanies',
          permission: 'externalCompanies.view',
          menu: {
            resource: 'externalCompanies',
            group: '/app/management',
            labelKey: 'menu.externalCompanies',
            order: 8,
          },
        },
        loadComponent: () =>
          import('./views/management/external-companies/external-companies.component').then(
            (m) => m.ExternalCompaniesComponent,
          ),
      },
      {
        // §17 — puestas a disposición de una EST, del lado de la empresa
        // usuaria (consultar, registrar, terminar y anular contra
        // `/v1/est-placements`). Sigue SIN entrada de menú: el empleador
        // responde el §17 con la nómina (columna EST/Contratista) y el filtro
        // de reportes, como en la evidencia ya entregada. La EST la ve en su
        // menú por `est/workers`.
        path: 'external-workers',
        data: {
          titleKey: 'menu.estWorkers',
          permission: 'estPlacements.view',
          scope: 'company',
        },
        loadComponent: () =>
          import('./views/management/est-placements/est-workers.component').then(
            (m) => m.EstWorkersComponent,
          ),
      },
      {
        // §29 — catálogo de jefaturas firmantes (nombre, correo y firma). Es
        // el origen del selector de firmantes al crear un documento y de la
        // firma que va en el documento final del §34. Vive en el API
        // (`document-signers`), con su propio módulo en la matriz (Gestión).
        path: 'document-signers',
        data: {
          titleKey: 'menu.documentSigners',
          permission: 'documentSigners.view',
          menu: {
            resource: 'documentSigners',
            group: '/app/documents-signatures',
            labelKey: 'menu.documentSigners',
            order: 3,
          },
        },
        loadComponent: () =>
          import('./views/management/document-signers/document-signers.component').then(
            (m) => m.DocumentSignersComponent,
          ),
      },
      {
        // Trazabilidad punta a punta de una marcación (RF-003 / RF-020).
        // `supervisor` queda fuera: la traza expone destinatarios de correo y
        // metadatos técnicos que su rol no requiere (no tiene clockings.view).
        path: 'clockings/:id',
        data: { titleKey: 'clockingTrace.title', permission: 'clockings.view' },
        loadComponent: () =>
          import('./views/attendance/clocking-detail/clocking-detail.component').then(
            (m) => m.ClockingDetailComponent,
          ),
      },
      {
        // Formulario de asignación: exige crear (ver asignaciones también lo
        // tiene el supervisor en el API, que no usa esta pantalla).
        path: 'shift-assignment',
        data: {
          titleKey: 'menu.managementSub.shiftAssignment',
          permission: 'shiftAssignments.create',
          menu: {
            resource: 'shiftAssignments',
            group: '/app/management',
            labelKey: 'menu.managementSub.shiftAssignment',
            order: 7,
          },
        },
        loadComponent: () =>
          import('./views/management/shift-assignment/shift-assignment.component').then(
            (m) => m.ShiftAssignment,
          ),
      },
      {
        // «Ajustes» (la bandeja de aprobación del empleador) se fusionó en
        // «Modificaciones de asistencia» el 04-10-2026: un componente por
        // concepto (contrato §9.11 del API). Redirección RELATIVA a propósito:
        // conserva la consulta de los enlaces guardados.
        path: 'adjustments',
        pathMatch: 'full',
        redirectTo: 'attendance-modification',
      },
      {
        // Modificaciones de asistencia (Arts. 39-41, §28): la ÚNICA pantalla de
        // las modificaciones — el empleador propone y aprueba las solicitudes del
        // trabajador («Solicitudes y propuestas», lo que era «Ajustes»), y el
        // trabajador acepta o se opone. Par empresa/propio del mismo concepto
        // (contrato §9.7): attendanceModification.view abre lo de la empresa y
        // self.adjustments.view lo de la propia ficha; cuál de las dos ve la
        // persona lo decide su alcance (DataScopeService), y las acciones sus
        // permisos (attendanceModification.create para proponer, .approve para
        // decidir las solicitudes, .batch para el llenado automático,
        // self.adjustments.oppose para responder).
        //
        // Entrada de primer nivel, junto a «Mi Asistencia», y no dentro de
        // «Gestión»: una sola entrada sirve a las dos partes, y el trabajador
        // necesita llegar sin el correo (sin dirección registrada el aviso no
        // llega y su derecho de oposición no puede depender de él).
        path: 'attendance-modification',
        data: {
          titleKey: 'menu.attendanceModification',
          anyPermission: ['attendanceModification.view', 'self.adjustments.view'],
          menu: {
            resource: 'attendanceModification',
            labelKey: 'menu.attendanceModification',
            icon: 'edit_note',
            module: 'dt',
            order: 30,
          },
        },
        loadComponent: () =>
          import('./views/attendance/attendance-modification/attendance-modification.component').then(
            (m) => m.AttendanceModification,
          ),
      },
      {
        path: 'alerts',
        data: {
          titleKey: 'menu.alerts',
          permission: 'alerts.view',
          menu: {
            resource: 'alerts',
            labelKey: 'menu.alerts',
            icon: 'notifications',
            module: 'dt',
            order: 50,
          },
        },
        loadComponent: () =>
          import('./views/attendance/alerts/alerts.component').then((m) => m.Alerts),
      },
      {
        // Documentos y Firmas › Documentos (Art. 47): UNA pantalla para la empresa y el
        // trabajador (el fiscalizador la abre en `dt/documents`). De quién son los datos lo
        // decide DataScopeService; qué puede hacer cada uno, sus permisos `laborDocuments.*` o
        // `self.documents.respond`. Los correos enlazan aquí con `?folio=`.
        // Una sola ruta para la empresa, el trabajador y el fiscalizador (FIS-01): de quién
        // son los documentos lo dice DataScopeService (empresa / propio / empresa
        // fiscalizada, que además trae sus solicitudes); qué puede hacer cada uno, sus
        // permisos (el fiscalizador no tiene ninguno de acción: sólo lectura).
        path: 'labor-documents',
        data: {
          titleKey: 'menu.documents',
          anyPermission: ['laborDocuments.view', 'self.documents.view'],
          inspector: true,
          menu: {
            resource: 'laborDocuments',
            group: '/app/documents-signatures',
            labelKey: 'menu.documents',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/documents/documents.component').then((m) => m.DocumentsComponent),
      },
      {
        // Documentos y Firmas › Solicitudes (§31, §33, ADM-06): el trabajador las presenta y la
        // empresa las resuelve en la misma pantalla.
        path: 'requests',
        data: {
          titleKey: 'menu.requests',
          anyPermission: ['requests.view', 'self.requests.view'],
          menu: {
            resource: 'requests',
            group: '/app/documents-signatures',
            labelKey: 'menu.requests',
            order: 2,
          },
        },
        loadComponent: () =>
          import('./views/requests/requests.component').then((m) => m.RequestsComponent),
      },
      // Rutas antiguas de las variantes por rol: los correos del API y los marcadores siguen
      // funcionando. Redirección relativa, para que conserve `?folio=` y demás parámetros.
      { path: 'worker/documents', redirectTo: 'labor-documents' },
      { path: 'worker/requests', redirectTo: 'requests' },
      {
        // ADM-07 — envío de notificaciones a trabajadores
        path: 'notifications',
        data: {
          titleKey: 'menu.notifications',
          permission: 'notifications.view',
          menu: {
            resource: 'notifications',
            group: '/app/configuration',
            labelKey: 'menu.notifications',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/notifications/notifications.component').then(
            (m) => m.NotificationsComponent,
          ),
      },
      {
        // Configuración › Log de Perfiles (Art. 15, `roleLog.view`): la MISMA pantalla de
        // auditoría con los eventos de perfil —asignación (incluida la inicial), definición de
        // perfiles y ajustes individuales—. El API entrega sólo esos eventos a quien tiene
        // `roleLog.view` sin `auditLogs.view`.
        path: 'role-log',
        data: {
          titleKey: 'menu.roleLog',
          permission: 'roleLog.view',
          auditCategory: 'all',
          menu: { resource: 'roleLog', group: '/app/auditing', labelKey: 'menu.roleLog', order: 2 },
        },
        loadComponent: () =>
          import('./views/audit/audit-log/audit-log.component').then((m) => m.AuditLogComponent),
      },
      {
        // Registro de cambios de contraseña (§6) — lo consulta la empresa, no
        // el trabajador. Lee la cadena real de audit-logs
        // (`GET /audit-logs/company/:id`).
        // Auditoría › Cambios de Contraseña (§6): la MISMA pantalla de auditoría con el filtro
        // de las dos acciones de cambio de clave (`data.auditActions`).
        path: 'password-log',
        data: {
          titleKey: 'menu.passwordLog',
          permission: 'auditLogs.view',
          auditActions: ['password_changed', 'password_reset_completed'],
          menu: {
            resource: 'auditLogs',
            group: '/app/auditing',
            labelKey: 'menu.passwordLog',
            order: 3,
          },
        },
        loadComponent: () =>
          import('./views/audit/audit-log/audit-log.component').then((m) => m.AuditLogComponent),
      },
      // ── «Dirección del Trabajo» (grupo /app/dt) ──────────────────────────
      // Un componente por concepto: los cinco reportes legales (Art. 27) son
      // UNA pantalla (`data.reportType`) para el trabajador, la empresa, la
      // EST, el fiscalizador y el staff en «Ver como». De quién son los datos
      // lo resuelve DataScopeService (propio / empresa / EST / empresa
      // fiscalizada), no la ruta; qué puede hacer cada uno, sus permisos
      // (exportar `reports.legal.export`, filtros avanzados). `staff` e
      // `inspector` son los mismos que declara el API en GET /reports/legal.
      {
        path: 'dt/portal',
        data: {
          titleKey: 'menu.inspectorPortal',
          inspector: true,
          menu: {
            resource: 'reports.legal',
            group: '/app/dt',
            labelKey: 'menu.inspectorPortal',
            order: 0,
          },
        },
        loadComponent: () =>
          import('./views/dt/portal/inspector-portal.component').then((m) => m.InspectorPortal),
      },
      {
        path: 'dt/reports/attendance',
        data: {
          titleKey: 'menu.reportsSub.attendance',
          reportType: 'attendance',
          permission: 'reports.legal.view',
          inspector: true,
          menu: {
            resource: 'reports.legal',
            group: '/app/dt',
            labelKey: 'menu.reportsSub.attendance',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/dt/legal-report/legal-report.component').then(
            (m) => m.LegalReportComponent,
          ),
      },
      {
        path: 'dt/reports/daily-schedule',
        data: {
          titleKey: 'menu.reportsSub.dailySchedule',
          reportType: 'dailySchedule',
          permission: 'reports.legal.view',
          inspector: true,
          menu: {
            resource: 'reports.legal',
            group: '/app/dt',
            labelKey: 'menu.reportsSub.dailySchedule',
            order: 2,
          },
        },
        loadComponent: () =>
          import('./views/dt/legal-report/legal-report.component').then(
            (m) => m.LegalReportComponent,
          ),
      },
      {
        path: 'dt/reports/sundays-holidays',
        data: {
          titleKey: 'menu.reportsSub.sundaysHolidays',
          reportType: 'sundaysHolidays',
          permission: 'reports.legal.view',
          inspector: true,
          menu: {
            resource: 'reports.legal',
            group: '/app/dt',
            labelKey: 'menu.reportsSub.sundaysHolidays',
            order: 3,
          },
        },
        loadComponent: () =>
          import('./views/dt/legal-report/legal-report.component').then(
            (m) => m.LegalReportComponent,
          ),
      },
      {
        path: 'dt/reports/shift-changes',
        data: {
          titleKey: 'menu.reportsSub.shiftChanges',
          reportType: 'shiftChanges',
          permission: 'reports.legal.view',
          inspector: true,
          menu: {
            resource: 'reports.legal',
            group: '/app/dt',
            labelKey: 'menu.reportsSub.shiftChanges',
            order: 4,
          },
        },
        loadComponent: () =>
          import('./views/dt/legal-report/legal-report.component').then(
            (m) => m.LegalReportComponent,
          ),
      },
      {
        // Art. 27 e — Reporte diario: el correo diario al empleador con las
        // marcaciones del día y su registro de envíos (módulo `dailyReports`).
        // Lo ven el empleador y el fiscalizador (la empresa fiscalizada, sólo
        // lectura); reenviar y configurar dependen de sus claves en la pantalla.
        path: 'dt/reports/daily',
        data: {
          titleKey: 'menu.reportsSub.daily',
          permission: 'dailyReports.view',
          inspector: true,
          menu: {
            resource: 'dailyReports',
            group: '/app/dt',
            labelKey: 'menu.reportsSub.daily',
            order: 4.5,
          },
        },
        loadComponent: () =>
          import('./views/dt/daily-report/daily-report.component').then(
            (m) => m.DailyReportComponent,
          ),
      },
      {
        // Art. 27 f: además del módulo de reportes exige el de incidentes
        // técnicos, como el API (la EST no lo tiene: tampoco el reporte).
        // Excepción de la prueba «un solo módulo» (app.routes.spec.ts).
        path: 'dt/reports/incidents',
        data: {
          titleKey: 'menu.reportsSub.incidents',
          reportType: 'incidents',
          permission: ['reports.legal.view', 'technicalIncidents.view'],
          inspector: true,
          menu: {
            resource: 'technicalIncidents',
            group: '/app/dt',
            labelKey: 'menu.reportsSub.incidents',
            order: 5,
          },
        },
        loadComponent: () =>
          import('./views/dt/legal-report/legal-report.component').then(
            (m) => m.LegalReportComponent,
          ),
      },
      {
        // §13 — el validador de hash (Art. 8) lo ven la empresa, el
        // trabajador, la EST/contratista, el fiscalizador y el staff. No hay
        // nada que autorizar: se sirve con `GET /clocking-receipts/validate/:hash`,
        // ruta PÚBLICA del API. Cada figura entra con la clave que ya tiene: la
        // empresa `hashValidator.view`, el trabajador `reports.legal.view` (su
        // módulo de reportes; no `self.clocking.create`, que es sólo marcar: la
        // plantilla de pre-lanzamiento marca sin ver el validador) y la
        // EST/contratista `reports.legal.filter`. Mezcla módulos, y por eso es
        // excepción de la prueba «un solo módulo» (app.routes.spec.ts).
        path: 'dt/hash-validator',
        data: {
          titleKey: 'menu.hashValidator',
          anyPermission: ['hashValidator.view', 'reports.legal.view', 'reports.legal.filter'],
          staff: 'compliance.read',
          inspector: true,
          menu: {
            resource: 'hashValidator',
            group: '/app/dt',
            labelKey: 'menu.hashValidator',
            order: 6,
          },
        },
        loadComponent: () =>
          import('./views/dt/hash-validator/hash-validator.component').then((m) => m.HashValidator),
      },
      {
        // Art. 26 — listado de empresas del fiscalizador (sólo lectura).
        path: 'dt/companies',
        data: {
          titleKey: 'menu.companiesListing',
          inspector: true,
          menu: {
            resource: 'reports.legal',
            group: '/app/dt',
            labelKey: 'menu.companiesListing',
            order: 7,
          },
        },
        loadComponent: () =>
          import('./views/dt/companies/inspector-companies.component').then(
            (m) => m.InspectorCompanies,
          ),
      },
      {
        // Auditoría › Accesos del fiscalizador: los fiscalizadores que
        // consultaron la empresa (GET /inspection/access-logs; la descarga, con
        // `inspectionAccess.export` dentro de la pantalla). El Art. 22.5 lo
        // llama «auditoría de acceso» y se lo exige al proveedor: no es un
        // reporte del Art. 27, por eso no va en «Dirección del Trabajo».
        path: 'inspector-access',
        data: {
          titleKey: 'menu.inspectorAccess',
          permission: 'inspectionAccess.view',
          menu: {
            resource: 'inspectionAccess',
            group: '/app/auditing',
            labelKey: 'menu.inspectorAccess',
            order: 4,
          },
        },
        loadComponent: () =>
          import('./views/audit/inspector-access/inspector-access.component').then(
            (m) => m.InspectorAccessComponent,
          ),
      },
      // URLs anteriores (marcadores, correos, portal del fiscalizador): las
      // redirecciones RELATIVAS conservan los parámetros de consulta.
      { path: 'reports/:type', redirectTo: 'dt/reports/:type' },
      { path: 'worker/reports/:type', redirectTo: 'dt/reports/:type' },
      { path: 'inspector/reports/:type', redirectTo: 'dt/reports/:type' },
      { path: 'est/reports/:type', redirectTo: 'dt/reports/:type' },
      { path: 'hash-validator', redirectTo: 'dt/hash-validator' },
      { path: 'inspector/hash-validator', redirectTo: 'dt/hash-validator' },
      { path: 'inspector/portal', redirectTo: 'dt/portal' },
      { path: 'inspector/companies', redirectTo: 'dt/companies' },
      { path: 'dt/inspector-access', redirectTo: 'inspector-access' },
      // Vistas de la EST (rol external): mismas claves que las de la empresa,
      // variante acotada a los trabajadores de la EST. Sus reportes legales
      // están en «Dirección del Trabajo» (dt/reports/*); «Trabajadores de
      // EST» usa su módulo `estPlacements`.
      {
        // §17 — la EST consulta sus trabajadores puestos a disposición
        // (`/v1/est-placements`: el API la acota a sus puestas y a sus
        // periodos). Módulo propio «Trabajadores de EST». El contratista no la
        // ve: el menú la filtra por la figura legal de la empresa (main-layout).
        path: 'est/workers',
        data: {
          titleKey: 'menu.estWorkers',
          permission: 'estPlacements.view',
          scope: 'est',
          menu: {
            resource: 'estPlacements',
            group: '/app/management',
            labelKey: 'menu.estWorkers',
            order: 9,
          },
        },
        loadComponent: () =>
          import('./views/management/est-placements/est-workers.component').then(
            (m) => m.EstWorkersComponent,
          ),
      },
      // URLs anteriores de los documentos del fiscalizador: la pantalla es la de la empresa.
      { path: 'inspection/documents', redirectTo: 'labor-documents' },
      { path: 'dt/documents', redirectTo: 'labor-documents' },
      { path: 'inspector/documents', redirectTo: 'labor-documents' },
      // La cuenta propia (§6). Por permisos, no por rol: las rutas `worker/*` de antes redirigen.
      {
        // «Mis correos» (Art. 34.2): correo personal (el de la cuenta), corporativo, de un
        // tercero, su estado por relación laboral y el historial. Cualquier sesión de producto:
        // en el API es `authenticatedOnly` (el trabajador debe poder SIEMPRE cambiar sus
        // correos). Spec: docs/spec-first/specs/my-emails.spec.md.
        path: 'account/emails',
        data: {
          titleKey: 'menu.myEmails',
          product: true,
          menu: {
            resource: 'self.profile',
            group: '/app/account',
            labelKey: 'menu.myEmails',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/account/emails/my-emails.component').then((m) => m.MyEmailsComponent),
      },
      // «Mi perfil» sólo cambiaba los correos: ahora lo hace «Mis correos» (un solo lugar).
      { path: 'account/profile', redirectTo: 'account/emails' },
      {
        // Cambio de la contraseña de acceso (requerimiento §6), en vista
        // propia dentro de Ajustes. Flujo REAL contra el API: valida la clave
        // actual, audita y manda el correo. Lo hace CUALQUIER usuario de la
        // empresa (`PATCH /auth/me/password` es `authenticatedOnly`): antes la
        // ruta pedía `self.profile.changeSecret` —la clave del PIN de
        // marcación— y RR.HH., el administrador y el supervisor no tenían dónde
        // cambiar su contraseña.
        path: 'account/password',
        data: {
          titleKey: 'menu.password',
          product: true,
          menu: {
            resource: 'self.profile',
            group: '/app/account',
            labelKey: 'menu.password',
            order: 2,
          },
        },
        loadComponent: () =>
          import('./views/account/password/password.component').then((m) => m.PasswordComponent),
      },
      {
        // Cambio del PIN de marcación por el propio trabajador (§6). Flujo
        // REAL contra el API (política, PIN actual, auditoría con IP, aviso
        // por correo y bitácora). SIN menú a propósito mientras se revisa si
        // el autoservicio del PIN corresponde; responde por URL.
        path: 'account/marking-pin',
        data: { titleKey: 'menu.markingPin', permission: 'self.profile.changeSecret' },
        loadComponent: () =>
          import('./views/account/marking-pin/marking-pin.component').then(
            (m) => m.MarkingPinComponent,
          ),
      },
      // URLs anteriores de la cuenta propia (correos y marcadores ya enviados).
      // Directo a «Mis correos»: el router aplica una sola redirección por nivel.
      { path: 'worker/profile', redirectTo: 'account/emails' },
      { path: 'worker/password', redirectTo: 'account/password' },
      { path: 'worker/marking-pin', redirectTo: 'account/marking-pin' },
      {
        // Ruta vieja de la respuesta del trabajador: la enlazan los correos de
        // propuesta ya enviados (`?intent=approve|oppose`, API
        // adjustments.service `_notifyWindowOpened`). Redirección RELATIVA a
        // propósito: conserva la consulta (lo comprueba
        // attendance-modification.component.spec.ts).
        path: 'worker/adjustments',
        pathMatch: 'full',
        redirectTo: 'attendance-modification',
      },
      {
        // Auditoría completa: ver la bitácora Y verificar la cadena (el log de
        // perfiles ADM-09 sólo exige ver).
        path: 'audit',
        data: {
          titleKey: 'menu.auditLog',
          permission: ['auditLogs.view', 'auditLogs.verify'],
          menu: {
            resource: 'auditLogs',
            group: '/app/auditing',
            labelKey: 'menu.auditLog',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/audit/audit-log/audit-log.component').then((m) => m.AuditLogComponent),
      },
      // Billing (SaaS subscription) — never exposed to the DT inspector
      // (fiscalización has no billing surface at all)
      {
        path: 'billing/subscription',
        data: {
          titleKey: 'menu.mySubscription',
          permission: 'subscription.view',
          menu: {
            resource: 'subscription',
            group: '/app/billing',
            labelKey: 'menu.mySubscription',
            order: 1,
          },
        },
        loadComponent: () =>
          import('./views/billing/my-subscription/my-subscription.component').then(
            (m) => m.MySubscription,
          ),
      },
      {
        path: 'billing/invoices',
        data: {
          titleKey: 'menu.myInvoices',
          permission: 'invoices.view',
          menu: {
            resource: 'invoices',
            group: '/app/billing',
            labelKey: 'menu.myInvoices',
            order: 2,
          },
        },
        loadComponent: () =>
          import('./views/billing/my-invoices/my-invoices.component').then((m) => m.MyInvoices),
      },
      {
        // Retorno del checkout Flow: sólo llega quien inició un pago.
        path: 'billing/payment-result',
        data: { titleKey: 'menu.myInvoices', permission: 'invoices.pay' },
        loadComponent: () =>
          import('./views/billing/payment-result/payment-result.component').then(
            (m) => m.PaymentResult,
          ),
      },
      // Paywall: cualquier sesión autenticada puede caer acá tras un 402
      // (sin requisitos: sólo autenticación). Quién puede pagar lo decide
      // invoices.pay dentro de la pantalla.
      {
        path: 'billing/suspended',
        data: { titleKey: 'menu.suspended' },
        loadComponent: () =>
          import('./views/billing/suspended/suspended.component').then((m) => m.Suspended),
      },
      // Términos de una cuenta en prueba: sólo exige sesión. trialTermsGuard manda aquí al
      // administrador mientras /access/me diga termsPending (diseño «contratación en prueba»).
      {
        path: 'billing/terms',
        data: { titleKey: 'menu.trialTerms' },
        loadComponent: () =>
          import('./views/billing/trial-terms/trial-terms.component').then(
            (m) => m.TrialTermsComponent,
          ),
      },
    ],
  },
  // Company inspection deep link (`inspectionUrl`, e.g. /inspect/76123456-7).
  // Guarded like every other inspector route: only an already-authenticated
  // inspector with a valid token gets through — it switches the company under
  // inspection instead of touching the session. Anyone else is bounced to
  // /login (authGuard) or /app/sin-acceso (accessGuard), never logged out.
  {
    path: 'inspect/:rut',
    canActivate: [authGuard, accessGuard],
    data: { inspector: true },
    loadComponent: () =>
      import('./views/dt/inspect-redirect/inspect-redirect.component').then(
        (m) => m.InspectRedirectComponent,
      ),
  },
  // Legacy redirect (previous /home entry point)
  {
    path: 'home',
    redirectTo: '/app',
  },
  {
    path: '**',
    redirectTo: '/app',
  },
];
